import { intersection } from 'src/utils/set/intersection';
import { union } from 'utils/set/union';

export type GeneratePrimaryKey<T> = (obj: T) => string;

export interface Options {
  matchAll?: boolean;
}

export type Callback<T> = (entry: T) => void;

/**
 * Create a Map that is indexable by tags.
 */
export function createTaggedMap<T, K extends keyof T>(
  {
    batchCallbacks = true,
    onEntryAdded,
    onEntryRemoved,
    primaryKey = <K>'id',
  }: {
    batchCallbacks?: boolean;
    onEntryAdded?: Callback<T | T[]>;
    onEntryRemoved?: Callback<T | T[]>;
    primaryKey?: K;
  } = { primaryKey: <K>'id' },
) {
  const map = new Map<T[K], T>();
  const tagSets = new Map<string, Set<T[K]>>();

  const { flush, isBatching, stack, startBatching } = createStacks({
    added: onEntryAdded,
    removed: onEntryRemoved,
  });

  /**
   * Callback for when an entry is added. This either adds the entry
   * to the "added" stack, or just calls the callback immediately.
   */
  function entryAddedCallback(entry: T) {
    if (batchCallbacks && isBatching()) {
      return stack('added')(entry);
    }

    return onEntryAdded?.(entry);
  }

  /**
   * Callback for when an entry is removed. This either adds the entry
   * to the "removed" stack, or just calls the callback immediately.
   */
  function entryRemovedCallback(entry: T) {
    if (batchCallbacks && isBatching()) {
      return stack('removed')(entry);
    }

    return onEntryRemoved?.(entry);
  }

  return {
    addOne,
    addMany: batch(addMany),
    batch,
    getAll,
    getManyByTags,
    getMatchingKeys,
    getMap,
    getMany,
    getOne,
    removeAll: batch(removeAll),
    removeMany: batch(removeMany),
    removeManyByTags: batch(removeManyByTags),
    removeOne,
  };

  /**
   * Flush callback stacks after calling a given method.
   */
  function batch<V extends (...args: any[]) => any>(fn: V): V {
    return <V>((...args: any[]) => {
      startBatching();

      const response = fn(...args);

      flush();

      return response;
    });
  }

  /**
   * Add many entries.
   */
  function addMany(newEntries: T[], tags?: string[][]): T[K][] {
    const keys = newEntries.map((newEntry, ndx) => addOne(newEntry, tags?.[ndx]));

    return keys;
  }

  /**
   * Add one entry.
   */
  function addOne(newEntry: T, tags?: string[]): T[K] {
    const key = newEntry[primaryKey];

    const existingEntry = map.get(key);

    if (existingEntry) {
      map.delete(key);
      entryRemovedCallback?.(existingEntry);
    }

    tags?.forEach((tag) => {
      // Add a new tagSet if it doesn't exist.
      if (!tagSets.get(tag)) {
        tagSets.set(tag, new Set());
      }

      // Add the key to the new tagSet.
      tagSets.get(tag)?.add(key);
    });

    map.set(key, newEntry);
    entryAddedCallback?.(newEntry);

    return key;
  }

  /**
   * Create stacks to temporarily hold added and removed entries. This allows
   * callbacks to be batched to avoid unnecessary calls.
   */
  function createStacks(params: Record<string, Callback<T | T[]> | undefined>) {
    let batching = false;
    const callbackStacks = new Map<string, { entries: Set<T>; callback?: Callback<T | T[]> }>();

    Object.entries(params).forEach(([key, callback]) => {
      if (callback) {
        callbackStacks.set(key, { callback, entries: new Set<T>() });
      }
    });

    return {
      flush: flushEntries,
      isBatching() {
        return batching;
      },
      stack: stackEntry,
      startBatching: startBatchingEntries,
    };

    /**
     * Create a function to add an entry to its relevant stack.
     */
    function stackEntry(type: string) {
      return function entryModified(entry: T) {
        callbackStacks.get(type)?.entries.add(entry);
      };
    }

    /**
     * Start batching the entries.
     */
    function startBatchingEntries() {
      batching = true;
    }

    /**
     * Flush entries of all stacks.
     */
    function flushEntries() {
      callbackStacks.forEach((ofType) => {
        const entries = Array.from(ofType.entries.values());

        if (entries.length) {
          ofType.callback?.(entries);
          ofType.entries.clear();
        }

        // Stop batching entries after flushing.
        batching = false;
      });
    }
  }

  /**
   * Get all entries.
   */
  function getAll(): T[] {
    return Array.from(map.values());
  }

  /**
   * Get many entries.
   */
  function getMany(keys: T[K][]): T[] {
    return <T[]>keys.map((key) => map.get(key)).filter((marking) => marking !== undefined);
  }

  /**
   * Get the internal Map object.
   */
  function getMap() {
    return map;
  }

  /**
   * Get a single entry.
   */
  function getOne(key: T[K]): T | undefined {
    return map.get(key);
  }

  /**
   * Get many entries by tags. Pass the option `matchAll: true` to get only those entries that match all provided tags.
   */
  function getManyByTags(tags: string[], options: Options = {}): T[] {
    // Return the entries from the map.
    return <T[]>getMatchingKeys(tags, options)
      .map((key) => map.get(key))
      .filter((x) => x !== undefined);
  }

  /**
   * Get the keys that match the provided paramaters.
   */
  function getMatchingKeys(tags: string[], options: Options = {}): T[K][] {
    const { matchAll } = options;

    // Get all the sets of keys for the provided tags
    const sets = <Set<T[K]>[]>tags.map((tag) => tagSets.get(tag)).filter((x) => x !== undefined);

    let keys: T[K][];

    // Get the keys from the sets, either by getting any that match, or all that match.
    if (!matchAll) {
      keys = Array.from(union<T[K]>(...sets));
    } else {
      keys = Array.from(intersection(...sets));
    }

    return keys;
  }

  /**
   * Clear the map, firing callbacks for any remaining entries.
   */
  function removeAll(): boolean {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    for (const [_, entry] of map.entries()) {
      entryRemovedCallback?.(entry);
    }

    map.clear();
    tagSets.clear();

    return true;
  }

  /**
   * Remove many entries by keys.
   */
  function removeMany(keys: T[K][]): boolean {
    return keys.every(removeOne);
  }

  /**
   * Remove many entries by providing tags to match against.
   */
  function removeManyByTags(tags: string[], options: Options = {}): boolean {
    return getMatchingKeys(tags, options).every((key) => removeOne(key));
  }

  /**
   * Remove one entry by its key.
   */
  function removeOne(key: T[K]): boolean {
    const existingEntry = map.get(key);

    if (existingEntry) {
      map.delete(key);
      entryRemovedCallback?.(existingEntry);
    }

    const entries = tagSets.entries();

    for (const [tag, tagSet] of entries) {
      if (tagSet.has(key)) {
        tagSet.delete(key);
      }

      // If there are no keys in the tagSet, it is not needed any more.
      if (tagSets.get(tag)?.size === 0) {
        tagSets.delete(tag);
      }
    }

    return true;
  }
}
